<script setup>
import { computed } from "vue";
import { Tooltip } from "@/components/ui/tooltip";
import { renderMarkdownInline } from "@utils/renderMarkdown";

// One populationsBarData entry: { label, color, data: [{ code, label, value 0..1, count, color? }] }
const props = defineProps({
  population: { type: Object, default: null },
});

const MEDALS = ["🥇", "🥈", "🥉"];
const HEIGHTS = [112, 76, 56];

const podium = computed(() => {
  const pop = props.population;
  if (!pop) return [];
  const shown = (d) => Math.round(d.count || 0);
  const sorted = [...pop.data]
    .filter((d) => d.code !== "999" && !d.isSubtotal)
    .sort((a, b) => shown(b) - shown(a) || (b.value || 0) - (a.value || 0));
  const places = [];
  for (const d of sorted) {
    const last = places[places.length - 1];
    if (last && shown(last) === shown(d)) {
      last.answers.push(d);
      continue;
    }
    if (places.length === 3) break;
    places.push({ value: d.value, count: d.count, answers: [d] });
  }
  const ranked = places.map((place, rank) => ({
    ...place,
    rank,
    medal: MEDALS[rank],
    height: HEIGHTS[rank],
    color: place.answers.find((d) => d.color)?.color || pop.color,
    label: place.answers[0].label,
    tied: place.answers.slice(1).map((d) => d.label),
  }));
  return ranked.length < 3 ? ranked : [ranked[1], ranked[0], ranked[2]];
});

function formatPercent(value) {
  return `${Math.round((value || 0) * 100)}%`;
}
</script>

<template>
  <div class="flex items-end justify-center gap-3 py-4">
    <Tooltip
      v-for="entry in podium"
      :key="entry.rank"
      :delay-duration="0"
      trigger-class="!cursor-default">
      <div class="flex w-36 flex-col items-center gap-1.5">
        <span class="text-xl">{{ entry.medal }}</span>
        <span class="text-2xl font-bold tabular-nums">{{ formatPercent(entry.value) }}</span>
        <div
          class="w-full rounded-t-md"
          :style="{ height: `${entry.height}px`, backgroundColor: entry.color }" />
        <span class="flex h-[30px] w-full items-baseline justify-center gap-1 text-center text-xs font-medium leading-[15px]">
          <span
            class="line-clamp-2 min-w-0 break-words"
            v-html="renderMarkdownInline(entry.label)" />
          <span
            v-if="entry.tied.length"
            class="shrink-0 text-muted-foreground">+{{ entry.tied.length }}</span>
        </span>
        <span class="text-3xs tabular-nums text-muted-foreground">
          n={{ Math.round(entry.count) }}<template v-if="entry.tied.length"> each</template>
        </span>
      </div>
      <template #content>
        <div class="flex min-w-[140px] flex-col gap-0.5 text-xs">
          <span class="font-semibold tabular-nums">
            {{ entry.medal }} {{ formatPercent(entry.value) }} · n={{ Math.round(entry.count) }}<template v-if="entry.tied.length"> each</template>
          </span>
          <span
            v-for="answer in entry.answers"
            :key="answer.code"
            v-html="renderMarkdownInline(answer.label)" />
        </div>
      </template>
    </Tooltip>
  </div>
</template>
